/**
 * Time-based conversation grouping utility
 * Groups conversations into: Today, Yesterday, Last 7 Days, This Month, Older
 */

export interface Thread {
  id: string;
  title: string;
  lastMessage?: string;
  timestamp: Date;
  // Tenant/Project context - saved when conversation is created
  tenantId?: string;
  tenantName?: string;
  projectUuid?: string;
  projectName?: string;
}

// Tenant color mapping for visual indicators
export const TENANT_COLORS: Record<string, string> = {
  'promospark': '#4F46E5', // Indigo
  'appdev': '#10B981',     // Emerald/Green
  'pxp': '#F59E0B',        // Amber/Orange
  'nominode': '#8B5CF6',   // Purple
  'default': '#6B7280',    // Gray
};

/**
 * Get the color for a tenant
 */
export function getTenantColor(tenantId?: string): string {
  if (!tenantId) return TENANT_COLORS.default;
  const normalizedId = tenantId.toLowerCase();
  return TENANT_COLORS[normalizedId] || TENANT_COLORS.default;
}

export interface GroupedThreads {
  today: Thread[];
  yesterday: Thread[];
  lastWeek: Thread[];
  thisMonth: Thread[];
  older: Thread[];
}

/**
 * Groups threads by time period
 */
export function groupThreadsByTime(threads: Thread[]): GroupedThreads {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const grouped: GroupedThreads = {
    today: [],
    yesterday: [],
    lastWeek: [],
    thisMonth: [],
    older: [],
  };

  for (const thread of threads) {
    const threadDate = new Date(thread.timestamp);

    if (threadDate >= today) {
      grouped.today.push(thread);
    } else if (threadDate >= yesterday) {
      grouped.yesterday.push(thread);
    } else if (threadDate >= lastWeek) {
      grouped.lastWeek.push(thread);
    } else if (threadDate >= thisMonth) {
      grouped.thisMonth.push(thread);
    } else {
      grouped.older.push(thread);
    }
  }

  // Sort each group by timestamp (most recent first)
  for (const key of Object.keys(grouped) as Array<keyof GroupedThreads>) {
    grouped[key].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  return grouped;
}

/**
 * Get the display label for a time group
 */
export function getGroupLabel(group: keyof GroupedThreads): string {
  const labels: Record<keyof GroupedThreads, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    lastWeek: 'Last 7 Days',
    thisMonth: 'This Month',
    older: 'Older',
  };
  return labels[group];
}

/**
 * Check if a group has any threads
 */
export function hasThreads(grouped: GroupedThreads, group: keyof GroupedThreads): boolean {
  return grouped[group].length > 0;
}

/**
 * Get all non-empty groups in display order
 */
export function getNonEmptyGroups(grouped: GroupedThreads): Array<keyof GroupedThreads> {
  const order: Array<keyof GroupedThreads> = ['today', 'yesterday', 'lastWeek', 'thisMonth', 'older'];
  return order.filter(group => hasThreads(grouped, group));
}
